import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Any RTK Query hook whose argument carries a `list_filter` (currentPage/pageSize)
 * and whose response carries the rows plus a `list_info`.
 */
type ListQueryHook<TData> = (
  arg: any,
  options?: { skip?: boolean },
) => { data?: TData; isFetching: boolean };

type UseAccumulatedListArgs<TData, TRow> = {
  useListQuery: ListQueryHook<TData>;
  /** The request minus `listFilter` — pagination is owned by this hook. */
  arg: Record<string, unknown>;
  pageSize: number;
  /**
   * Serialized filter state. Any change resets the accumulator back to page 0, so
   * changing a date range or a filter never mixes rows from two different queries.
   */
  filterKey: string;
  selectRows: (data: TData) => TRow[] | undefined;
  selectTotal: (data: TData) => number | string | undefined;
  skip?: boolean;
};

export type AccumulatedList<TRow> = {
  /** Every row loaded so far, in page order. */
  rows: TRow[];
  totalItems: number;
  isFetching: boolean;
  hasMore: boolean;
  /** Request the next page. A no-op while a page is in flight or the list is exhausted. */
  loadNextPage: () => void;
};

/**
 * Incremental "load more as you scroll" list on top of a paginated RPC.
 *
 * The query stays keyed by page while the pages seen so far are accumulated in
 * local state, so the caller gets one growing array. Two guards keep that array
 * honest: the merge is idempotent per page index (StrictMode and fresh response
 * references re-run the effect), and `loadNextPage` fires at most once per
 * accumulated snapshot, so a virtualizer calling it on every measure pass cannot
 * skip a page.
 *
 * Rows are kept when `skip` flips to true, so switching away from a tab and back
 * lands on the list as it was rather than on page 0.
 */
export function useAccumulatedList<TData, TRow>({
  useListQuery,
  arg,
  pageSize,
  filterKey,
  selectRows,
  selectTotal,
  skip = false,
}: UseAccumulatedListArgs<TData, TRow>): AccumulatedList<TRow> {
  const [page, setPage] = useState(0);
  const [resetToken, setResetToken] = useState(0);
  const [accumulated, setAccumulated] = useState<{ rows: TRow[]; totalItems: number }>({
    rows: [],
    totalItems: 0,
  });

  const { data: pageData, isFetching } = useListQuery(
    { ...arg, listFilter: { currentPage: page, pageSize, sortBy: "severity" } },
    { skip },
  );

  const mergedPageRef = useRef(-1);
  const prevFilterKeyRef = useRef(filterKey);
  // Guards loadNextPage against advancing more than once per accumulated snapshot.
  const lastRequestedCountRef = useRef(0);

  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      mergedPageRef.current = -1;
      lastRequestedCountRef.current = 0;
      setPage(0);
      setAccumulated((prev) => (prev.rows.length > 0 ? { rows: [], totalItems: 0 } : prev));
      setResetToken((token) => token + 1);
      return;
    }
    // Merge each page exactly once, and only after its fetch has settled (otherwise
    // pageData may still hold the previous page's rows).
    if (skip || !pageData || isFetching) return;
    if (mergedPageRef.current === page) return;
    mergedPageRef.current = page;
    const rows = selectRows(pageData) ?? [];
    setAccumulated((prev) => ({
      // Page 0 replaces rather than appends, so a reset that raced the fetch cannot
      // leave duplicates behind.
      rows: page === 0 ? [...rows] : [...prev.rows, ...rows],
      totalItems: Number(selectTotal(pageData) ?? 0),
    }));
  }, [pageData, page, isFetching, filterKey, skip, selectRows, selectTotal, resetToken]);

  const hasMore = accumulated.rows.length < accumulated.totalItems;

  const loadNextPage = useCallback(() => {
    if (skip || isFetching || !hasMore) return;
    if (lastRequestedCountRef.current === accumulated.rows.length) return;
    lastRequestedCountRef.current = accumulated.rows.length;
    setPage((prev) => prev + 1);
  }, [skip, isFetching, hasMore, accumulated.rows.length]);

  return {
    rows: accumulated.rows,
    totalItems: accumulated.totalItems,
    isFetching: skip ? false : isFetching,
    hasMore,
    loadNextPage,
  };
}
